import type { ApiResponse } from '@redirector/shared';

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string>;

  constructor(status: number, code: string, message: string, fields: Record<string, string> = {}) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.fields = fields;
  }
}

let csrfToken: string | null = null;
let unauthorizedHandler: () => void = () => {};

/** The token is issued by login and held in memory only, never in storage. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function onUnauthorized(handler: () => void): void {
  unauthorizedHandler = handler;
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type Envelope<T> = { data: T; meta: Record<string, unknown> };

/** The code the server sends when the CSRF header is missing or stale. */
const CSRF_FAILED = 'csrf_failed';

/** In flight while a refresh is running, so concurrent saves share one. */
let refreshing: Promise<boolean> | null = null;

/**
 * Re-reads the session's CSRF token from the server.
 *
 * The token is issued at login and kept in memory, so it is absent for any
 * page whose JavaScript started after the session did — most obviously a
 * reload. `GET /api/session` reports the token for the current cookie, which
 * makes recovery a single request rather than a forced sign-in.
 */
async function refreshCsrfToken(): Promise<boolean> {
  if (refreshing !== null) return refreshing;

  refreshing = (async () => {
    try {
      const response = await fetch('/api/session', { credentials: 'same-origin' });
      const payload = (await response.json()) as ApiResponse<{ csrfToken?: string | null }>;
      if (!payload.success) return false;

      const token = payload.data.csrfToken;
      if (typeof token !== 'string' || token === '') return false;
      csrfToken = token;
      return true;
    } catch {
      // A refresh is a best effort. Failing it just means the original error
      // stands, which is the behaviour there was before.
      return false;
    } finally {
      refreshing = null;
    }
  })();

  return refreshing;
}

async function attempt<T>(method: Method, path: string, body?: unknown): Promise<Envelope<T>> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken !== null) headers['x-csrf-token'] = csrfToken;

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiRequestError(
      0,
      'network',
      'Could not reach the server. Check your connection and try again.',
    );
  }

  if (response.status === 401) {
    unauthorizedHandler();
  }

  let payload: ApiResponse<T> | null = null;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiRequestError(
      response.status,
      'unexpected_response',
      'The server returned an unexpected response.',
    );
  }

  if (payload.success) return { data: payload.data, meta: payload.meta ?? {} };

  throw new ApiRequestError(
    response.status,
    payload.error.code,
    payload.error.message,
    payload.error.fields ?? {},
  );
}

/**
 * One request, retried once if it failed CSRF verification.
 *
 * A stale or missing token is a recoverable condition, and it used to be a
 * dead end: the cookie stayed valid, so nothing signed the operator out, and
 * every save answered `csrf_failed` until they found the reload the message
 * asked for — including the sign-out that would have fixed it, because a 403
 * is not a 401. Recovering it here means the console repairs itself instead of
 * telling someone to.
 *
 * Exactly one retry, and only when the refresh produced a DIFFERENT token: a
 * server that rejects the token it just issued is refusing for some other
 * reason, and retrying that forever would turn one failed save into a loop.
 */
async function requestEnvelope<T>(
  method: Method,
  path: string,
  body?: unknown,
): Promise<Envelope<T>> {
  try {
    return await attempt<T>(method, path, body);
  } catch (error) {
    const recoverable =
      error instanceof ApiRequestError && error.code === CSRF_FAILED && method !== 'GET';
    if (!recoverable) throw error;

    const stale = csrfToken;
    if (!(await refreshCsrfToken()) || csrfToken === stale) throw error;

    return attempt<T>(method, path, body);
  }
}

const request = async <T>(method: Method, path: string, body?: unknown): Promise<T> =>
  (await requestEnvelope<T>(method, path, body)).data;

export const apiGet = <T>(path: string): Promise<T> => request<T>('GET', path);

export const apiSend = <T>(
  method: Exclude<Method, 'GET'>,
  path: string,
  body?: unknown,
): Promise<T> => request<T>(method, path, body);

/**
 * `apiGet`, but keeping the envelope's `meta` alongside the data.
 *
 * Pagination cursors genuinely are metadata rather than part of the resource,
 * so the audit log reads them from here instead of the endpoint being reshaped
 * to fold `nextCursor` into its payload.
 */
export const apiGetWithMeta = <T>(path: string): Promise<Envelope<T>> =>
  requestEnvelope<T>('GET', path);
